import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, downloadFile, formatApiErrorDetail } from "@/lib/api";
import DoubleCheckDialog from "@/components/DoubleCheckDialog";
import { STATUS_LABELS, STATUS_COLORS, PRIORITY_LABELS, PRIORITY_COLORS, EDITABLE_STATUSES, formatDate, SLA_LABELS, SLA_TEXT, SLA_BAR, RECURRENCE_LABELS, RECURRENCE_COLORS, PLAN_STATUS_LABELS, PLAN_STATUS_COLORS, PLAN_ORIGIN_LABELS, AI_VERDICT_LABELS, AI_VERDICT_COLORS, PHOTO_AI_STATUS_LABELS, PHOTO_AI_STATUS_COLORS, AI_CONFIDENCE_LABELS, OCC_CLASS_LABELS, OCC_CLASS_COLORS } from "@/lib/labels";
import ConclusionDialog from "@/components/ConclusionDialog";
import RecordActions from "@/components/RecordActions";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import ReviewDialog from "@/components/ReviewDialog";
import OccurrenceEditDialog from "@/components/OccurrenceEditDialog";
import { EvidenceGallery, BeforeAfterSlider } from "@/components/EvidenceGallery";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, MapPin, Send, History, MessageSquare, ImageIcon, CheckCircle2, XCircle, ShieldCheck, PenLine, Timer, UserCheck, AlertTriangle, Pause, ClipboardCheck, Sparkles, Zap, ExternalLink, Columns2, FileDown } from "lucide-react";

function AiValidationPanel({ ai, overrideReason }) {
  return (
    <div className="mt-4 pt-4 border-t border-border/60" data-testid="ai-validation-card">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <p className="text-sm font-semibold flex items-center gap-1.5"><Sparkles size={14} className="text-primary" /> Validação Inteligente de Evidências</p>
        {ai.available ? (
          <>
            <Badge variant="outline" className={AI_VERDICT_COLORS[ai.verdict]} data-testid="ai-verdict-badge">
              {AI_VERDICT_LABELS[ai.verdict]}
            </Badge>
            <Badge variant="outline" data-testid="ai-score-badge">Pontuação {ai.score}/100</Badge>
            <span className="text-xs text-muted-foreground">{AI_CONFIDENCE_LABELS[ai.confidence] || ""}</span>
          </>
        ) : (
          <Badge variant="outline" className="bg-slate-500/15 text-slate-400 border-slate-500/30" data-testid="ai-unavailable-badge">
            Análise automática indisponível — validação manual
          </Badge>
        )}
      </div>
      {ai.available && (
        <>
          {ai.summary && (
            <p className="text-xs text-muted-foreground italic mb-2" data-testid="ai-summary">"{ai.summary}"</p>
          )}
          <div className="space-y-1">
            {(ai.photos || []).map((p, i) => (
              <p key={i} className="text-xs flex items-start gap-1.5" data-testid={`ai-photo-verdict-${i}`}>
                <span className={`font-semibold shrink-0 ${PHOTO_AI_STATUS_COLORS[p.status] || ""}`}>
                  Foto {(p.index ?? i) + 1} — {PHOTO_AI_STATUS_LABELS[p.status] || p.status} ({p.score}/100):
                </span>
                <span className="text-muted-foreground">{p.justification}</span>
              </p>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            Análise por {ai.model} em {formatDate(ai.analyzed_at)} · parecer sugestivo — a decisão final é do aprovador.
          </p>
        </>
      )}
      {!ai.available && ai.note && <p className="text-xs text-muted-foreground">{ai.note}</p>}
      {overrideReason && (
        <p className="text-xs text-amber-400 mt-2 flex items-start gap-1.5" data-testid="ai-override-record">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>Aprovada contra a recomendação da IA. Justificativa do aprovador: "{overrideReason}"</span>
        </p>
      )}
    </div>
  );
}

export default function OccurrenceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [occ, setOcc] = useState(null);
  const [mySettings, setMySettings] = useState(null);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [conclusionOpen, setConclusionOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectMode, setRejectMode] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [dcOpen, setDcOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      await downloadFile(`/reports/occurrence/${id}/pdf`, `ocorrencia-${occ?.protocol || id}.pdf`);
      toast.success("PDF da ocorrência gerado");
    } catch {
      toast.error("Falha ao gerar o PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const load = () => api.get(`/occurrences/${id}`).then(({ data }) => setOcc(data)).catch(() => navigate("/ocorrencias"));
  useEffect(() => {
    load();
    api.get("/settings/me").then(({ data }) => setMySettings(data));
  }, [id]);

  if (!occ || !mySettings) {
    return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  const { can, evidence } = mySettings;
  const req = occ.conclusion_request;
  const isEditableStatus = EDITABLE_STATUSES.includes(occ.status);
  const aiVal = req?.ai_validation;
  const aiAgainst = !!(aiVal?.available && ["reprovado", "requer_revisao"].includes(aiVal.verdict));

  const changeStatus = async (status) => {
    try {
      await api.patch(`/occurrences/${id}/status`, { status });
      toast.success(`Status alterado para ${STATUS_LABELS[status]}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const approve = async (doubleCheck = null) => {
    try {
      await api.post(`/occurrences/${id}/approve-conclusion`, { override_reason: overrideReason, double_check: doubleCheck });
      toast.success("Conclusão aprovada — ocorrência concluída!");
      setOverrideMode(false);
      setOverrideReason("");
      setDcOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reject = async () => {
    if (rejectReason.trim().length < 3) return toast.error("Informe o motivo da rejeição");
    try {
      await api.post(`/occurrences/${id}/reject-conclusion`, { reason: rejectReason });
      toast.success("Conclusão rejeitada — ocorrência voltou para Em andamento");
      setRejectMode(false);
      setRejectReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendComment = async () => {
    if (!comment.trim()) return;
    setSending(true);
    try {
      await api.post(`/occurrences/${id}/comments`, { text: comment });
      setComment("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="occurrence-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="detail-back-btn">
        <ArrowLeft size={15} /> Voltar
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-heading text-2xl font-bold tracking-tight font-mono" data-testid="detail-protocol">{occ.protocol}</h1>
            <Badge variant="outline" className={STATUS_COLORS[occ.status]} data-testid="detail-status-badge">{STATUS_LABELS[occ.status]}</Badge>
            <Badge variant="outline" className={PRIORITY_COLORS[occ.priority]}>{PRIORITY_LABELS[occ.priority]}</Badge>
            <Badge variant="outline" className={OCC_CLASS_COLORS[occ.occurrence_class || "cliente"]} data-testid="detail-class-badge">
              {OCC_CLASS_LABELS[occ.occurrence_class || "cliente"]}
            </Badge>
            {occ.recurrence?.level && occ.recurrence.level !== "normal" && (
              <Badge variant="outline" className={RECURRENCE_COLORS[occ.recurrence.level]} data-testid="detail-recurrence-badge">
                {RECURRENCE_LABELS[occ.recurrence.level]} · {occ.recurrence.count}ª
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1.5">
            Criada por {occ.created_by_name} · <span data-testid="detail-occurred-at">Data da Ocorrência: {formatDate(occ.occurred_at || occ.created_at)}</span> · Registro: {formatDate(occ.registered_at || occ.created_at)}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <RecordActions
            module="ocorrencias"
            testId="occurrence-detail"
            onEdit={() => setEditOpen(true)}
            onReview={() => setReviewOpen(true)}
            onDelete={() => setDeleteOpen(true)}
          />
          <Button variant="outline" onClick={exportPdf} disabled={pdfLoading} data-testid="occurrence-pdf-btn">
            <FileDown size={15} className="mr-2" /> {pdfLoading ? "Gerando..." : "PDF"}
          </Button>
          {isEditableStatus && (
            <Select value={occ.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-full sm:w-44" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EDITABLE_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {isEditableStatus && can.request_conclusion && (
            <Button onClick={() => setConclusionOpen(true)} data-testid="request-conclusion-btn">
              <CheckCircle2 size={15} className="mr-2" /> Solicitar Conclusão
            </Button>
          )}
        </div>
      </div>

      {occ.status === "aguardando_aprovacao" && req && (
        <Card className="p-5 border-cyan-500/40 bg-cyan-500/5" data-testid="conclusion-review-card">
          <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2 text-cyan-400">
            <ShieldCheck size={16} /> Solicitação de conclusão aguardando aprovação
          </h3>
          <div className="text-sm space-y-2">
            <p><span className="text-muted-foreground">Solicitada por:</span> {req.requested_by_name} em {formatDate(req.requested_at)}</p>
            {req.description && <p><span className="text-muted-foreground">Ação corretiva:</span> {req.description}</p>}
            {req.signature && <p className="flex items-center gap-1.5"><PenLine size={13} className="text-muted-foreground" /> Assinado por {req.signature}</p>}
            {req.gps && <p className="flex items-center gap-1.5"><MapPin size={13} className="text-muted-foreground" /> GPS: {req.gps.lat}, {req.gps.lng}</p>}
            {req.photos?.length > 0 && (
              <div className="pt-1">
                <EvidenceGallery photos={req.photos} cols="grid-cols-4 sm:grid-cols-6" testId="conclusion-evidence-gallery" />
              </div>
            )}
          </div>
          {req.ai_validation && <AiValidationPanel ai={req.ai_validation} />}
          {can.approve_conclusion && (
            <div className="mt-4 pt-4 border-t border-border/60 space-y-3">
              {overrideMode && (
                <div className="space-y-2" data-testid="ai-override-panel">
                  <p className="text-xs text-amber-400 flex items-center gap-1.5">
                    <AlertTriangle size={13} /> A IA recomendou revisão das evidências. Para aprovar mesmo assim, informe a justificativa obrigatória:
                  </p>
                  <div className="flex gap-2">
                    <Textarea rows={2} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="Justificativa para aprovar contra a recomendação da IA..." className="resize-none" data-testid="ai-override-reason-input" />
                    <div className="flex flex-col gap-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                        disabled={overrideReason.trim().length < 3} onClick={() => (occ.double_check_required ? setDcOpen(true) : approve())} data-testid="ai-override-confirm-btn">Aprovar</Button>
                      <Button size="sm" variant="ghost" onClick={() => setOverrideMode(false)}>Cancelar</Button>
                    </div>
                  </div>
                </div>
              )}
              {!rejectMode && !overrideMode ? (
                <div className="flex gap-3 flex-wrap items-center">
                  <Button onClick={() => (aiAgainst ? setOverrideMode(true) : occ.double_check_required ? setDcOpen(true) : approve())} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-conclusion-btn">
                    <CheckCircle2 size={15} className="mr-2" /> Aprovar Conclusão
                  </Button>
                  <Button variant="outline" onClick={() => setRejectMode(true)} data-testid="reject-conclusion-btn">
                    <XCircle size={15} className="mr-2" /> Rejeitar
                  </Button>
                  {occ.double_check_required && (
                    <span className="text-xs text-cyan-400 flex items-center gap-1.5" data-testid="double-check-required-hint">
                      <ShieldCheck size={13} /> Double Check em campo obrigatório (foto + GPS no local)
                    </span>
                  )}
                </div>
              ) : rejectMode ? (
                <div className="flex gap-2">
                  <Textarea rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Motivo da rejeição..." className="resize-none" data-testid="reject-reason-input" />
                  <div className="flex flex-col gap-2">
                    <Button size="sm" variant="destructive" onClick={reject} data-testid="reject-confirm-btn">Confirmar</Button>
                    <Button size="sm" variant="ghost" onClick={() => setRejectMode(false)}>Cancelar</Button>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </Card>
      )}

      {occ.status === "concluido" && occ.approval && (
        <Card className="p-4 border-emerald-500/40 bg-emerald-500/5" data-testid="approval-info-card">
          <p className="text-sm flex items-center gap-2 text-emerald-400">
            <CheckCircle2 size={15} />
            Conclusão aprovada por {occ.approval.approved_by_name} em {formatDate(occ.approval.approved_at)}
            {req?.requested_by_name && <span className="text-muted-foreground">· solicitada por {req.requested_by_name}</span>}
          </p>
          {req?.ai_validation && <AiValidationPanel ai={req.ai_validation} overrideReason={occ.approval.ai_override_reason} />}
          {occ.approval.double_check && (
            <div className="mt-2 pt-2 border-t border-emerald-500/20 flex items-center gap-2 flex-wrap text-xs text-muted-foreground" data-testid="double-check-info">
              <ShieldCheck size={13} className="text-cyan-400" />
              <span>
                Double Check em campo validado por {occ.approval.double_check.validated_by}
                {occ.approval.double_check.distance_m != null && <> · a {Math.round(occ.approval.double_check.distance_m)} m do local (tolerância {occ.approval.double_check.tolerance_m} m)</>}
              </span>
              {(occ.approval.double_check.photos || []).map((p, i) => (
                <img key={i} src={p} alt={`double check ${i + 1}`} className="h-10 w-10 rounded object-cover border border-border" />
              ))}
            </div>
          )}
        </Card>
      )}

      <DoubleCheckDialog open={dcOpen} onOpenChange={setDcOpen} occGeo={occ.geo}
        toleranceM={occ.double_check_tolerance_m || 100} onConfirm={(dc) => approve(dc)} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Cliente", occ.client_name], ["Unidade", occ.unit_name], ["Área", occ.area_name], ["Subárea", occ.subarea_name]].map(([k, v]) => (
          <Card key={k} className="px-4 py-3 border-border bg-card">
            <p className="text-xs text-muted-foreground">{k}</p>
            <p className="font-medium text-sm mt-0.5 truncate">{v}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {occ.sla_info && (
          <Card className="p-4 border-border bg-card" data-testid="detail-sla-card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
                <Timer size={15} className="text-primary" /> SLA — {occ.sla_info.hours}h
              </h3>
              <span className={`text-xs font-semibold ${occ.sla_info.paused && !occ.sla_info.concluded ? "text-violet-400" : SLA_TEXT[occ.sla_info.color]}`}>
                {occ.sla_info.paused && !occ.sla_info.concluded ? "Pausado" : SLA_LABELS[occ.sla_info.color]}
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden mb-2">
              <div className={`h-full rounded-full transition-all ${SLA_BAR[occ.sla_info.color]}`} style={{ width: `${Math.min(occ.sla_info.pct, 100)}%` }} />
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Decorrido: {occ.sla_info.elapsed_hours}h ({Math.min(occ.sla_info.pct, 999)}%)</span>
              <span>
                {occ.sla_info.concluded
                  ? (occ.sla_info.pct >= 100 ? "Concluída fora do prazo" : "Concluída no prazo")
                  : occ.sla_info.pct >= 100
                    ? `Estourado há ${Math.abs(occ.sla_info.remaining_hours).toFixed(0)}h`
                    : `Restam ${occ.sla_info.remaining_hours.toFixed(0)}h`}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span>Prazo final: {formatDate(occ.sla_info.deadline)}</span>
              {occ.sla_info.paused && !occ.sla_info.concluded && (
                <span className="flex items-center gap-1 text-violet-400"><Pause size={11} /> contagem pausada</span>
              )}
              {occ.sla_escalation_level > 0 && (
                <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-500/30" data-testid="detail-escalation-badge">
                  Escalonado nível {occ.sla_escalation_level}
                </Badge>
              )}
            </div>
          </Card>
        )}
        <Card className="p-4 border-border bg-card" data-testid="detail-responsible-card">
          <h3 className="font-heading font-semibold text-sm flex items-center gap-2 mb-2">
            <UserCheck size={15} className="text-primary" /> Responsável
          </h3>
          {occ.responsible_name ? (
            <p className="text-sm font-medium">{occ.responsible_name}</p>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum responsável mapeado para este local.</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">Atribuído automaticamente pelo mapeamento de responsáveis (Cliente → Unidade → Área → Subárea).</p>
        </Card>
      </div>

      {occ.recurrence?.level && occ.recurrence.level !== "normal" && (
        <Card className="p-4 border-amber-500/40 bg-amber-500/5" data-testid="detail-recurrence-card">
          <p className="text-sm flex items-start gap-2 text-amber-400">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>
              {RECURRENCE_LABELS[occ.recurrence.level]}: {occ.recurrence.count}ª ocorrência de {occ.category} neste local.
              {occ.recurrence.related?.length > 0 && (
                <span className="block text-xs text-muted-foreground mt-1">Anteriores: {occ.recurrence.related.join(", ")}</span>
              )}
            </span>
          </p>
        </Card>
      )}

      {occ.action_plan && (
        <Card className="p-4 border-primary/40 bg-primary/5" data-testid="linked-plan-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h3 className="font-heading font-semibold text-sm flex items-center gap-2 flex-wrap">
                <ClipboardCheck size={15} className="text-primary" /> Plano de Ação Vinculado
                <span className="font-mono text-primary">{occ.action_plan.number}</span>
                <Badge variant="outline" className={PLAN_STATUS_COLORS[occ.action_plan.status]} data-testid="linked-plan-status">
                  {PLAN_STATUS_LABELS[occ.action_plan.status]}
                </Badge>
                {occ.action_plan.auto_generated && (
                  <Badge variant="outline" className="bg-amber-500/15 text-amber-400 border-amber-500/30">
                    <Zap size={11} className="mr-1" /> {PLAN_ORIGIN_LABELS[occ.action_plan.origin] || "Automático"}
                  </Badge>
                )}
              </h3>
              <p className="text-sm mt-1">{occ.action_plan.title}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                <span>Responsável: {occ.action_plan.responsible_name || "—"}</span>
                <span>Criado: {formatDate(occ.action_plan.created_at)}</span>
                <span>Prazo: {formatDate(occ.action_plan.deadline)}</span>
                <span>Ações: {occ.action_plan.progress?.actions_done || 0}/{occ.action_plan.progress?.actions_total || 0} concluídas</span>
              </div>
              <div className="flex items-center gap-2 mt-2 max-w-64">
                <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${occ.action_plan.progress?.pct || 0}%` }} />
                </div>
                <span className="text-xs font-semibold">{occ.action_plan.progress?.pct || 0}%</span>
              </div>
              {(occ.action_plan.timeline || []).length > 0 && (
                <div className="mt-2.5 space-y-0.5">
                  {occ.action_plan.timeline.slice(-3).map((h, i) => (
                    <p key={i} className="text-[11px] text-muted-foreground truncate">• {h.detail} — {formatDate(h.timestamp)}</p>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate(`/planos-de-acao/${occ.action_plan.id}`)} data-testid="open-plan-btn">
              <ExternalLink size={13} className="mr-1.5" /> Abrir Plano de Ação
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-5 border-border bg-card">
        <h3 className="font-heading font-semibold text-sm mb-2">Descrição · {occ.category}{occ.type_name ? ` · ${occ.type_name}` : ""}{occ.subtype_name ? ` · ${occ.subtype_name}` : ""}</h3>
        <p className="text-sm whitespace-pre-wrap text-muted-foreground" data-testid="detail-description">{occ.description}</p>
        {occ.custom_fields && Object.keys(occ.custom_fields).length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="detail-custom-fields">
            {Object.entries(occ.custom_fields).map(([k, v]) => (
              <div key={k} className="rounded-md border border-border bg-muted/30 px-2.5 py-1.5">
                <p className="text-[9px] uppercase tracking-wide text-muted-foreground">{k.replace(/_/g, " ")}</p>
                <p className="text-xs font-medium">{v === true ? "Sim" : v === false ? "Não" : String(v)}</p>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4 mt-3">
          {occ.origin && <p className="text-xs text-muted-foreground">Origem: {occ.origin}</p>}
          {occ.location && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <MapPin size={13} className="text-red-400" /> Localização marcada no mapa ({occ.location.x}%, {occ.location.y}%)
            </p>
          )}
        </div>
      </Card>

      {occ.photos?.length > 0 && (
        <Card className="p-5 border-border bg-card">
          <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2"><ImageIcon size={15} /> Fotos da ocorrência ({occ.photos.length})</h3>
          <EvidenceGallery photos={occ.photos} testId="occurrence-photos-gallery" />
        </Card>
      )}

      {occ.correction_photos?.length > 0 && (
        <Card className="p-5 border-border bg-card">
          <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2"><CheckCircle2 size={15} className="text-emerald-400" /> Fotos da correção ({occ.correction_photos.length})</h3>
          <EvidenceGallery photos={occ.correction_photos} testId="correction-photos-gallery" />
        </Card>
      )}

      {occ.photos?.length > 0 && occ.correction_photos?.length > 0 && (
        <Card className="p-5 border-border bg-card" data-testid="before-after-card">
          <h3 className="font-heading font-semibold text-sm mb-1 flex items-center gap-2"><Columns2 size={15} className="text-primary" /> Comparativo Antes vs Depois</h3>
          <p className="text-xs text-muted-foreground mb-3">Arraste o divisor para comparar a situação encontrada com a correção executada.</p>
          <BeforeAfterSlider
            before={occ.photos[0]}
            after={occ.correction_photos[occ.correction_photos.length - 1]}
            beforeLabel="Antes (ocorrência)"
            afterLabel="Depois (correção)"
          />
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5 border-border bg-card">
          <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2"><MessageSquare size={15} /> Comentários ({occ.comments?.length || 0})</h3>
          <div className="space-y-3 max-h-72 overflow-y-auto scrollbar-thin mb-4">
            {(occ.comments || []).map((c) => (
              <div key={c.id} className="rounded-lg bg-muted/50 px-3.5 py-2.5" data-testid="detail-comment">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold">{c.user}</p>
                  <p className="text-[10px] text-muted-foreground">{formatDate(c.timestamp)}</p>
                </div>
                <p className="text-sm mt-1">{c.text}</p>
              </div>
            ))}
            {(!occ.comments || occ.comments.length === 0) && <p className="text-sm text-muted-foreground">Nenhum comentário.</p>}
          </div>
          <div className="flex gap-2">
            <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Escreva um comentário..."
              className="resize-none" data-testid="comment-input" />
            <Button onClick={sendComment} disabled={sending || !comment.trim()} size="icon" className="shrink-0 self-end" data-testid="comment-submit-btn">
              <Send size={15} />
            </Button>
          </div>
        </Card>

        <Card className="p-5 border-border bg-card">
          <h3 className="font-heading font-semibold text-sm mb-4 flex items-center gap-2"><History size={15} /> Histórico</h3>
          <div className="space-y-0 max-h-96 overflow-y-auto scrollbar-thin">
            {(occ.history || []).slice().reverse().map((h, i) => (
              <div key={i} className="flex gap-3 pb-4 last:pb-0 relative" data-testid="history-entry">
                <div className="flex flex-col items-center">
                  <div className="h-2.5 w-2.5 rounded-full bg-primary mt-1 shrink-0" />
                  {i < occ.history.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                </div>
                <div className="pb-1">
                  <p className="text-sm font-medium">{h.detail}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{h.user} · {formatDate(h.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {user.role === "cliente" && (
        <p className="text-xs text-muted-foreground text-center pb-4">
          Como cliente, você pode alterar o status, comentar e solicitar a conclusão com evidências. A conclusão final depende da aprovação do supervisor.
        </p>
      )}

      <ConclusionDialog open={conclusionOpen} onOpenChange={setConclusionOpen} occId={id} evidence={evidence} onDone={load} />
      <OccurrenceEditDialog open={editOpen} onOpenChange={setEditOpen} occurrence={occ} onSaved={load} />
      <ReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} entity="ocorrência" entityLabel={occ.protocol}
        url={`/occurrences/${id}/review`} onDone={load} />
      <ConfirmDeleteDialog open={deleteOpen} onOpenChange={setDeleteOpen} entity="ocorrência" entityLabel={occ.protocol}
        impactUrl={`/occurrences/${id}/delete-impact`} deleteUrl={`/occurrences/${id}`}
        requireReason onDeleted={() => navigate("/ocorrencias")} />
    </div>
  );
}
